import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  BookOpen, Clock, CheckCircle, XCircle, Eye, Calendar,
  ArrowLeft, Loader2, ShoppingBag
} from 'lucide-react';
import { Button } from './ui/button';
import SecurePDFViewer from './SecurePDFViewer';

const BACKEND_URL = window.location.origin;

const PurchaseHistory = ({ onBack }) => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [purchases, setPurchases] = useState([]);
  const [pendingPurchases, setPendingPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingBook, setViewingBook] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPurchaseHistory();
    }
  }, [isAuthenticated]);

  const fetchPurchaseHistory = async () => {
    setLoading(true);
    try {
      // Fetch completed purchases with book details
      const purchasesRes = await fetch(`${BACKEND_URL}/api/books/user/purchase-history`, {
        credentials: 'include'
      });
      if (purchasesRes.ok) {
        const data = await purchasesRes.json();
        setPurchases(data.purchases || []);
      }

      // Fetch pending purchases
      const pendingRes = await fetch(`${BACKEND_URL}/api/books/user/pending-purchases`, {
        credentials: 'include'
      });
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingPurchases(data.pending || []);
      }
    } catch (error) {
      console.error('Failed to fetch purchase history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReadBook = (purchase) => {
    setViewingBook({
      book_id: purchase.book_id,
      title: purchase.book_title
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#251845] to-[#1f1340] py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <ShoppingBag className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">سجل المشتريات</h2>
          <p className="text-gray-400 mb-6">يرجى تسجيل الدخول لعرض مشترياتك</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#251845] to-[#1f1340] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-pink-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#251845] to-[#1f1340] py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white" dir="rtl">سجل المشتريات</h1>
            <p className="text-gray-400" dir="rtl">عرض جميع الكتب التي اشتريتها</p>
          </div>
        </div>

        {/* Pending Purchases */}
        {pendingPurchases.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2" dir="rtl">
              <Clock className="w-5 h-5" />
              طلبات قيد الانتظار ({pendingPurchases.length})
            </h2>
            <div className="space-y-3">
              {pendingPurchases.map((purchase) => (
                <div 
                  key={purchase.request_id}
                  className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                        <Clock className="w-6 h-6 text-yellow-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium" dir="rtl">{purchase.book_title}</h3>
                        <p className="text-gray-400 text-sm flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(purchase.created_at).toLocaleDateString('ar-EG')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="bg-yellow-500/20 text-yellow-400 text-sm px-3 py-1 rounded-full">
                        في انتظار التفعيل
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Purchases */}
        <div>
          <h2 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2" dir="rtl">
            <CheckCircle className="w-5 h-5" />
            الكتب المشتراة ({purchases.length})
          </h2>
          
          {purchases.length === 0 ? (
            <div className="bg-gray-800/50 rounded-xl p-12 text-center border border-gray-700">
              <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl text-gray-400 mb-2" dir="rtl">لا توجد مشتريات بعد</h3>
              <p className="text-gray-500" dir="rtl">ستظهر الكتب التي تشتريها هنا</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {purchases.map((purchase) => (
                <div 
                  key={purchase.purchase_id}
                  className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden hover:border-pink-500/30 transition-colors"
                >
                  <div className="flex">
                    {/* Book Cover */}
                    <div className="w-24 h-32 bg-gray-700 flex-shrink-0">
                      {purchase.cover_url ? (
                        <img 
                          src={`${BACKEND_URL}${purchase.cover_url}`} 
                          alt={purchase.book_title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-8 h-8 text-gray-500" />
                        </div>
                      )}
                    </div>
                    
                    {/* Book Info */}
                    <div className="flex-1 p-4 flex flex-col justify-between">
                      <div>
                        <h3 className="text-white font-semibold line-clamp-1" dir="rtl">
                          {purchase.book_title}
                        </h3>
                        <p className="text-gray-400 text-sm" dir="rtl">{purchase.author}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          <span>تم الشراء: {new Date(purchase.purchased_at).toLocaleDateString('ar-EG')}</span>
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => handleReadBook(purchase)}
                        className="mt-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-sm"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        اقرأ الآن
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Secure PDF Viewer */}
      {viewingBook && (
        <SecurePDFViewer
          bookId={viewingBook.book_id}
          bookTitle={viewingBook.title}
          onClose={() => setViewingBook(null)}
        />
      )}
    </div>
  );
};

export default PurchaseHistory;
